import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FaCheckCircle, FaAward, FaUsers, FaLightbulb, FaHeart } from 'react-icons/fa';
import './About.css';

const About = () => {
  const [achievementValues, setAchievementValues] = useState({
    years: 0,
    projects: 0,
    brands: 0,
    satisfaction: 0
  });
  const achievementsRef = useRef(null);

  const values = [
    {
      icon: <FaLightbulb />,
      title: 'Innovation',
      description: 'We constantly push the limits in audio visual and automation technology, staying ahead of industry trends.'
    },
    {
      icon: <FaUsers />,
      title: 'Customer Satisfaction',
      description: 'Our dedication to customer satisfaction is paramount. We deeply understand each client\'s unique needs.'
    },
    {
      icon: <FaHeart />,
      title: 'Integrity',
      description: 'We uphold the highest standards of honesty, transparency, and ethical practices in all our dealings.'
    },
    {
      icon: <FaAward />,
      title: 'Excellence',
      description: 'We are committed to offering top-tier products and services coupled with unparalleled professionalism.'
    }
  ];

  const achievements = [
    { number: '7+', label: 'Years of Experience' },
    { number: '450+', label: 'Projects Completed' },
    { number: '20+', label: 'Premium Brands' },
    { number: '98%', label: 'Client Satisfaction' }
  ];

  // Counting animation effect
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Reset values first
            setAchievementValues({
              years: 0,
              projects: 0,
              brands: 0,
              satisfaction: 0
            });
            
            // Add delay before starting animations
            setTimeout(() => {
              // Animate years
              let yearsCount = 0;
              const yearsInterval = setInterval(() => {
                yearsCount++;
                setAchievementValues(prev => ({ ...prev, years: yearsCount }));
                if (yearsCount >= 7) clearInterval(yearsInterval);
              }, 100);

              // Animate projects
              let projectsCount = 0;
              const projectsInterval = setInterval(() => {
                projectsCount += 10;
                setAchievementValues(prev => ({ ...prev, projects: projectsCount }));
                if (projectsCount >= 450) {
                  setAchievementValues(prev => ({ ...prev, projects: 450 }));
                  clearInterval(projectsInterval);
                }
              }, 20);

              // Animate brands
              let brandsCount = 0;
              const brandsInterval = setInterval(() => {
                brandsCount++;
                setAchievementValues(prev => ({ ...prev, brands: brandsCount }));
                if (brandsCount >= 20) clearInterval(brandsInterval);
              }, 80);

              // Animate satisfaction
              let satisfactionCount = 0;
              const satisfactionInterval = setInterval(() => {
                satisfactionCount += 2;
                setAchievementValues(prev => ({ ...prev, satisfaction: satisfactionCount }));
                if (satisfactionCount >= 98) {
                  setAchievementValues(prev => ({ ...prev, satisfaction: 98 }));
                  clearInterval(satisfactionInterval);
                }
              }, 20);
            }, 300); // 300ms delay before starting
          }
        });
      },
      { threshold: 0.3 }
    );

    if (achievementsRef.current) {
      observer.observe(achievementsRef.current);
    }

    return () => {
      if (achievementsRef.current) {
        observer.unobserve(achievementsRef.current);
      }
    };
  }, []); // Removed hasAnimated dependency

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="about-hero">
        <div className="about-hero-overlay"></div>
        <div className="about-hero-content">
          <h1>About F&H Audio Solutions LLP</h1>
          <p>
            Where Luxury and Innovation Collide in High-End Home Audio-Visual Systems and Automation Solutions
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="about-story">
        <div className="container">
          <div className="story-grid">
            <div className="story-content">
              <h2></h2>
              <p>
                Welcome to F&H Audio Solutions LLP, a place where luxury and innovation collide in the 
                domain of High-end Home audio-visual system solutions and Automation solution Consultancy. 
                Being a top supplier of outstanding audio-visual experiences, we take great pleasure in 
                designing customized home audio-visual and automation solutions that transform the way you 
                engage with your house and enjoy entertainment.
              </p>
              <p>
                At F&H Audio Solutions, we are passionate about innovation and constantly push the limits 
                in audio visual and automation technology. Our dedication to customer satisfaction is 
                paramount, and we take the time to deeply understand each client's unique desires and 
                preferences, ensuring our solutions meet and surpass their expectations.
              </p>
              <p>
                We recognize that every home and individual is unique, and our approach is deeply 
                personalized, with every solution crafted meticulously to align with each client's 
                specific lifestyle and preferences. We are also committed to environmental responsibility 
                and actively pursue eco-friendly technologies and sustainable practices.
              </p>
            </div>
            <div className="story-image">
              <img 
                src="https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=800&h=600&fit=crop&q=80" 
                alt="F&H Audio Solutions" 
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="about-values">
        <div className="container">
          <div className="section-header">
            <h2>Our Core Values</h2>
            <p>The principles that guide everything we do</p>
          </div>
          <div className="values-grid">
            {values.map((value, index) => (
              <div className="value-card" key={index}>
                <div className="value-icon">{value.icon}</div>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Achievements Section */}
      <section className="about-achievements" ref={achievementsRef}>
        <div className="container">
          <div className="achievements-grid">
            <div className="achievement-item">
              <h3>{achievementValues.years}+</h3>
              <p>Years of Experience</p>
            </div>
            <div className="achievement-item">
              <h3>{achievementValues.projects}+</h3>
              <p>Projects Completed</p>
            </div>
            <div className="achievement-item">
              <h3>{achievementValues.brands}+</h3>
              <p>Premium Brands</p>
            </div>
            <div className="achievement-item">
              <h3>{achievementValues.satisfaction}%</h3>
              <p>Client Satisfaction</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="about-why-choose">
        <div className="container">
          <div className="why-choose-grid">
            <div className="why-choose-image">
              <img 
                src="https://images.unsplash.com/photo-1593784991095-a205069470b6?w=800&h=600&fit=crop&q=80" 
                alt="Why Choose Us" 
              />
            </div>
            <div className="why-choose-content">
              <h2>Why Choose F&H Audio Solutions?</h2>
              <ul className="why-choose-list">
                <li>
                  <FaCheckCircle />
                  <div>
                    <strong>7+ Years of Expertise</strong>
                    <span>Over a 7 years of experience in premium audio-visual installations</span>
                  </div>
                </li>
                <li>
                  <FaCheckCircle />
                  <div>
                    <strong>Certified Professionals</strong>
                    <span>Our team holds certifications from leading audio brands</span>
                  </div>
                </li>
                <li>
                  <FaCheckCircle />
                  <div>
                    <strong>Premium Brand Partnerships</strong>
                    <span>Authorized dealers of world-renowned audio brands</span>
                  </div>
                </li>
                <li>
                  <FaCheckCircle />
                  <div>
                    <strong>Custom Solutions</strong>
                    <span>Every project is tailored to your specific needs and preferences</span>
                  </div>
                </li>
                <li>
                  <FaCheckCircle />
                  <div>
                    <strong>Comprehensive Service</strong>
                    <span>From consultation to installation and ongoing support</span>
                  </div>
                </li>
                <li>
                  <FaCheckCircle />
                  <div>
                    <strong>Quality Assurance</strong>
                    <span>Rigorous testing and calibration for optimal performance</span>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="about-cta">
        <div className="container">
          <h2>Let's Create Something Extraordinary Together</h2>
          <p>Experience the F&H Audio Solutions difference. Contact us today to discuss your project.</p>
          <div className="cta-buttons">
            <Link to="/contact" className="btn btn-primary">
              Get Started
            </Link>
            <a href="tel:+919876543210" className="btn btn-secondary">
              Call Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
